/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import PropTypes from 'prop-types';
import { ContentCopyOutlined, DeleteRounded, EditOutlined, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { renderCell } from '../../Datagrid/table_config';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { strippingTableColumns } from './table_config';
import { APP_ACTION_TYPES, CONTROL_TABLE_PARAMETER_MAP, HIDE_TARGET_ECD, HYDRAULICS_FULL_ACCESS_PERMISSIONS, HYDRAULICS_TRIP_PIPE_TYPE, HYDRAULICS_TRIP_PIPE_TYPE_VALUE, MAX_ROW_LIMITS, STRIPPING_REPORT_EVERY_SI, STRIPPING_REPORT_EVERY_US } from '../../../../utils/constants';
import SelectEditValueTypes from '../../SelectEditValueTypes';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { addNewStrippingRow, createStrippings, deleteStrippings, listStripping, updateStripping } from '../../../../store/actions/stripping';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import { COLORS } from '../../../../shared/config/colors';
import SelectDrillDetails from '../../SelectDrillDetails';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import TargetModal from '../TargetModal';
import { checkPermissions } from '../../../../utils/checkPermissions';
function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}
const StrippingModal = ({
  selectedJob,
  rowData,
  proxyLoader,
  setAccordionProperty,
  data,
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { unit, convertUnitSystem } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const stripping = useSelector((state) => state.stripping);
  const depthPoints = hydraulics?.units?.results?.depth_points;
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [runDetails, setRundetails] = useState([]);
  const observation = useSelector((state) => state.observation);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [anchorPoint, setAnchorPoint] = useState('None');
  const [targetECD, setTargetECD] = useState('');
  const [targetData, setTargetData] = useState({});
  const [selectedRow, setSelectedRow] = useState({});

  useEffect(() => {
    if (rowData?.run_details) {
      const { drillStrings = [], drillFluids = [] } = hydraulics?.drillDetails?.hydraulicsDrillDetails || {};
      const drillString = drillStrings.find(item => item._id === rowData?.run_details.drillStringsId) || drillStrings[0];
      const drillFluid = drillFluids.find(item => item._id === rowData?.run_details.drillFluidsId) || drillFluids[0];
      const runDetails = {
        ...rowData.run_details,
        _id: generateRandom(),
        rpm: rowData?.run_details?.rpm ?? 0.0,
        correction_factor: rowData?.run_details?.correction_factor ?? 1.0,
        bit_depth: rowData?.run_details?.bit_depth ?? (rowData?.run_details?.hole_depth + rowData?.run_details?.bit_depth_offset),
        drillStringsId: drillString
          ? `${drillString.name.trim()} @ ${drillString.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${depthPoints?.md?.label}`
          : '',
        drillFluidsId: drillFluid
          ? `${drillFluid.name.trim()} @ ${drillFluid.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${depthPoints?.md?.label}`
          : ''
      };
      setRundetails([runDetails]);
    }
  }, [rowData]);

  useEffect(() => {
    if (!stripping.loading) {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listStripping(payload));
    }
  }, []);

  const getPipeConfig = (configValue) => {
    return Object.keys(HYDRAULICS_TRIP_PIPE_TYPE_VALUE).find(
      (key) => HYDRAULICS_TRIP_PIPE_TYPE_VALUE[key] === configValue
    ) || configValue;
  };

  useEffect(() => {
    if (stripping?.data && !hydraulics?.details?.loading && !stripping?.loading) {
      const { drillFluids = [] } = hydraulics?.drillDetails?.hydraulicsDrillDetails || {};
      let hasDeletedFillingFluid = false;
      const formatFluid = (fluid) =>
        `${fluid.name.trim()} @ ${fluid.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${hydraulics?.drillDetails?.units?.MD?.label}`;
      const reportIntervalsList = unit === 'SI' ? STRIPPING_REPORT_EVERY_SI : STRIPPING_REPORT_EVERY_US;
      const stripData = stripping.data.map((item) => {
        let report_interval;
        const isValidInterval = reportIntervalsList.includes(item?.report_interval);
        if (!isValidInterval) {
          report_interval = reportIntervalsList[2];
          item.isDirty = true;
          setInvalidRows(prev => ({ ...prev, [item._id]: true }));
          dispatch(
            enqueueSnackbar(
              'Configured interval not found in the list. A default has been applied, please review and save.',
              'error',
              new Date().getTime() + Math.random()
            )
          );
        } else {
          report_interval = item?.report_interval;
        }
        if (!item.filling_fluid_id) {
          item.filling_fluid_id = hydraulics?.details?.data[0]?.drillFluidsId;
          item.isDirty = true;
        }
        const fillingFluidExists = drillFluids.some(fluid => fluid._id === item.filling_fluid_id);
        if (!fillingFluidExists) {
          hasDeletedFillingFluid = true;
          item.isDirty = true;
          item.filling_fluid_id = '';
        }
        const drillFillingFluid = drillFluids.find(drillItem => drillItem._id === item.filling_fluid_id);
        if (!drillFillingFluid) {
          setInvalidRows(prev => ({ ...prev, [item._id]: true }));
        }
        return {
          ...item,
          tubular: getPipeConfig(item.tubular),
          report_interval,
          filling_fluid_id: drillFillingFluid ? formatFluid(drillFillingFluid) : '',
        };
      });
      if (hasDeletedFillingFluid) {
        dispatch(
          enqueueSnackbar(
            'Configured Drillfluid in Stripping is not found, Please Check',
            'error',
            new Date().getTime() + Math.random()
          )
        );
      }
      setProperties(stripData);
      setAccordionProperty(stripData);
    }
  }, [stripping?.data, hydraulics?.details?.loading, unit]);

  useEffect(() => {
    if (properties[0]?.from === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'from', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'from');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  useEffect(() => {
    if (targetData?.anchor_point && selectedRow?._id) {
      setProperties(prevProperties => {
        const updatedProperties = prevProperties.map(item =>
          item._id === selectedRow._id
            ? {
                ...item,
                target_data: targetData,
                isDirty: true
              }
            : item
        );
        setAccordionProperty(updatedProperties);
        return updatedProperties;
      });
    }
  }, [targetData]);

  const handleAddParameter = () => {
    if (properties[0]?.isNewRow) return 0;
    dispatch(addNewStrippingRow(unit));
  };

  const updateData = (id, field, value) => {
    const updateFieldValue = (item) => {
      const isDirty = value !== item[field];
      return {
        ...item,
        [field]: ['speed', 'from', 'to', 'report_interval'].includes(field)
          ? Number(value)
          : value,
        isDirty,
      };
    };

    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item =>
        item._id === id ? updateFieldValue(item) : item
      );
      setAccordionProperty(updatedProperties);
      return updatedProperties;
    });
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateStrippingDetails = (details) => {
    const { _id, speed, from, to } = details;
    const min_speed = stripping.units.inputs.speed.min;
    const max_speed = stripping.units.inputs.speed.max;
    const min_from = stripping.units.inputs.from.min;
    const min_to = stripping.units.inputs.to.min;
    const holeDepth = runDetails[0]?.hole_depth;
    const unitLabel = stripping?.units?.inputs?.to?.label;
    if (speed < min_speed || speed > max_speed) return showError(`Speed should be between ${min_speed} and ${max_speed} ${stripping?.units?.inputs?.speed?.label}`, _id);
    if (from < min_from || from > holeDepth) {
      return showError(
        `From MD should be between 0 and ${holeDepth} ${unitLabel}.`,
        _id
      );
    }

    if (to < min_to || to > holeDepth) {
      return showError(
        `To MD should be between 0 and ${holeDepth} ${unitLabel}.`,
        _id
      );
    }

    if (from === to) {
      return showError(
        'From MD and To MD should not be the same.',
        _id
      );
    }

    return true;
  };

  const checkStrippingValues = (value) => {
    const row = value;
    const invalidFields = [];
    const isInvalidNumber = (val) => {
      // eslint-disable-next-line no-mixed-operators
      return isNaN(val) || val === '' || typeof val === 'string' && val.trim() === '';
    };

    if (row?.from === undefined || row?.from === null || isInvalidNumber(row.from)) {
      invalidFields.push('From MD');
    }
    if (row?.to === undefined || row?.to === null || isInvalidNumber(row.to)) {
      invalidFields.push('To MD');
    }
    if (row?.speed === undefined || row?.speed === null || isInvalidNumber(row.speed)) {
      invalidFields.push('Speed');
    }
    if (row?.filling_fluid_id === undefined || row?.filling_fluid_id === null || row.filling_fluid_id === '') {
      invalidFields.push('Filling Fluid');
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateStrippingDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const copyStripping = (row) => {
    setProperties((prev) => {
      const newRow = {
        ...row,
        runNumber: prev.length + 1,
        _id: generateRandom(),
        isNewRow: true,
        isDirty: true,
      };
      const updated = [...prev, newRow];
      setAccordionProperty(updated);
      return updated;
    });
  };

  const checkForDuplicates = (properties) => {
    const seen = new Set();
    for (let i = 0; i < properties.length; i++) {
      const { filling_fluid_id, to, from, report_interval, speed, tubular, target_data } = properties[i];
      const targetLabel = `${(!target_data || !target_data?.anchor_point || target_data?.anchor_point === 'None') ? 'None' : target_data?.anchor_point}@${target_data?.target_ecd}`;
      const key = `${from}|${to}|${filling_fluid_id}|${report_interval}|${speed}|${tubular}|${targetLabel}`;
      if (seen.has(key)) {
        setInvalidRows(prev => ({ ...prev, [properties[i]?._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Row already exisit',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
      seen.add(key);
    }
    return true;
  };

  const transformRunsECDSpan = (targetData, hydraulics) => {
    if (!targetData.ecd_span) return [];
    return targetData?.ecd_span?.map((row) => ({
      name: row?.name,
      low_emw: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.low_emw, hydraulics.units.inputs?.ecd_span?.low_emw?.conversionFactor) : Number(row.low_emw),
      high_emw: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.high_emw, hydraulics.units.inputs?.ecd_span?.high_emw?.conversionFactor) : Number(row.high_emw),
    }));
  };

  const transformControlsTable = (targetData, hydraulics) => {
    if (!targetData.controls_table) return [];
    return targetData?.controls_table?.map((row) => {
      const parameter = CONTROL_TABLE_PARAMETER_MAP[row?.parameter] || row?.parameter;
      return ({
        maintain: row?.maintain?.toLowerCase(),
        parameter,
        from: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.from, hydraulics.units.inputs?.target_data?.controls_table?.[parameter]?.conversionFactor) : Number(row.from),
        to: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.to, hydraulics.units.inputs?.target_data?.controls_table?.[parameter]?.conversionFactor) : Number(row.to),
        step_size: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.step_size, hydraulics.units.inputs?.target_data?.controls_table?.[parameter]?.conversionFactor) : Number(row.step_size),
      });
    });
  };

  const saveStripping = (row) => {
    if (checkStrippingValues(row) && checkForDuplicates(properties)) {
      const body = {
        from: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.from, stripping?.units?.inputs?.from?.conversionFactor) : Number(row.from),
        to: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.to, stripping?.units?.inputs?.to?.conversionFactor) : Number(row.to),
        speed: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.speed, stripping?.units?.inputs?.speed?.conversionFactor) : Number(row.speed),
        report_interval: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.report_interval, stripping?.units?.inputs?.report_interval?.conversionFactor) : Number(row.report_interval),
        tubular: HYDRAULICS_TRIP_PIPE_TYPE_VALUE[row?.tubular],
        filling_fluid_id: hydraulics?.drillDetails?.hydraulicsDrillDetails?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${hydraulics?.drillDetails?.units?.MD?.label}` === row?.filling_fluid_id)[0]._id,
      };
      if (row?.target_data && Object.keys(row?.target_data).length !== 0) {
        const updatedRunsECDSpan = transformRunsECDSpan(row?.target_data, hydraulics);
        const updatedControlsTable = transformControlsTable(row?.target_data, hydraulics);

        body.target_data = {
          anchor_point: row?.target_data?.anchor_point,
          target_ecd: row?.target_data?.target_ecd == null
            ? null
            : unit !== UNIT_SYSTEM.US
              ? convertUnitSystem(
                row?.target_data?.target_ecd,
                hydraulics.units.inputs?.target_data?.target_ecd?.conversionFactor
              )
              : Number(row?.target_data?.target_ecd),
          ecd_span: updatedRunsECDSpan,
          controls_table: updatedControlsTable
        };
      }
      const payload = {
        inputs: body,
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        ...(row?.isNewRow ? {} : { _id: row._id })
      };
      const hydraulicsBody = {
        isStrippingResult: rowData?.isStrippingResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      if (row?.isNewRow) {
        dispatch(createStrippings(payload, hydraulicsBody));
      } else {
        dispatch(updateStripping(payload, hydraulicsBody));
      }
      setAnchorPoint('None');
      setTargetECD(null);
      setTargetData({});
    }
  };

  const removeStripping = (row) => {
    if (row?.isNewRow) {
      const updatedParams = properties.filter(item => item._id !== row?._id);
      setAccordionProperty(updatedParams);
      setProperties(updatedParams);
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listStripping(payload));
    } else {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        _id: row._id
      };
      const hydraulicsBody = {
        isStrippingResult: rowData?.isStrippingResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      dispatch(deleteStrippings(payload, hydraulicsBody));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const handleAnchorPointTargetECD = (newAnchorPoint, newTargetECD) => {
    setAnchorPoint(newAnchorPoint);
    setTargetECD(newTargetECD);
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || stripping.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, stripping?.permissions))}
          onClick={() => {
            saveStripping(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={stripping.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.COPY, stripping?.permissions)) || properties.some(item => item.isDirty === true || item.isNewRow || properties?.length === MAX_ROW_LIMITS.STRIPPING)}
          onClick={() => {
            copyStripping(row);
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={stripping.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, stripping?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderTargetsEditAction = ({ row }) => {
    return (
        <Grid container justifyContent="center">
          <Grid item>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                variant="solid"
                disabled={observation.loading}
                onClick={() => {
                  if (row?.target_data?.anchor_point) {
                    setAnchorPoint(row?.target_data?.anchor_point);
                    setTargetECD(row?.target_data?.target_ecd);
                    setTargetData(row?.target_data);
                  }
                  setShowTargetModal(true);
                  dispatch({
                    type: REDUX_ACTIONS.OBSERVATION_LIST_LOADING,
                  });
                  setTimeout(() => {
                    setSelectedRow(row);
                  }, 100);
                }}
              >
                <EditOutlined />
              </IconButton>
              {!row?.target_data?.anchor_point || HIDE_TARGET_ECD.includes(row?.target_data?.anchor_point)
                ? (
                  <Typography variant="button" fontSize={'1rem'} fontWeight={900}>None</Typography>
                  )
                : (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="button" fontSize={'1rem'} fontWeight={900}>
                      {row?.target_data?.target_ecd} {hydraulics.units.inputs.target_data.target_ecd.label}
                    </Typography>
                    <Typography variant="button" fontSize={'1rem'} fontWeight={900}>
                      @ {row?.target_data?.anchor_point}
                    </Typography>
                  </Box>
                  )}
            </Box>
          </Grid>
        </Grid>
    );
  };

  const renderTripPipeTypeCell = (params) => {
    return (
      <SelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={HYDRAULICS_TRIP_PIPE_TYPE}
      />
    );
  };

  const renderReportEveryCell = (params) => {
    return (
      <SelectEditValueTypes
        {...params}
        enableSort={false}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={unit === UNIT_SYSTEM.US ? STRIPPING_REPORT_EVERY_US : STRIPPING_REPORT_EVERY_SI}
      />
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderDrillFluidCell = (params) => {
    const drillFluids = hydraulics?.drillDetails?.hydraulicsDrillDetails?.drillFluids || [];
    const drillFluidsWithAir = [...drillFluids];
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={drillFluidsWithAir}
        label={hydraulics?.drillDetails?.units?.MD?.label}
        decimals={hydraulics?.drillDetails?.units?.MD?.decimals}
      />
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <>
      <Grid container>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" marginLeft={'13px'}>
            <Typography
              variant="button"
              fontWeight="700"
              onClick={handleAddParameter}
              className={
                properties?.length === MAX_ROW_LIMITS.STRIPPING || properties.some(item => item.isDirty === true) || stripping?.loading || hydraulics?.details?.loading || hydraulics?.details?.data[0]?.isNewRow ||
                  !checkPermissions(APP_ACTION_TYPES.CREATE, stripping?.permissions)
                  ? classes.disableIcon
                  : ''
              }
              sx={{ cursor: 'pointer' }}
            >
              + Add
            </Typography>

            <Typography
              variant="h6"
              color={COLORS.LIGHT_GREY}
              fontWeight="500"
              sx={{ marginLeft: '12px' }}
            >
              {`(Note: Maximum ${MAX_ROW_LIMITS.STRIPPING} entries are allowed)`}
            </Typography>
          </Box>
        </Box>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                textAlign: 'justify',
                pointerEvents: proxyLoader ? 'none' : 'auto',
                backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem !important',
                fontWeight: '900 !important'
              },
            }}
            loading={stripping.loading || hydraulics?.details?.loading}
            columns={strippingTableColumns(renderValueCell, renderActionCell, stripping?.units?.inputs, unit, renderTripPipeTypeCell, renderReportEveryCell, renderCell, renderDrillFluidCell, renderTargetsEditAction)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            hideFooterPagination
            hideFooterSelectedRowCount
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            height={'25vh'}
            isRowSelectable={() => !proxyLoader}
            getRowClassName={(params) =>
              proxyLoader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
            }
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={stripping?.permissions}
          />
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeStripping}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      {showTargetModal
        ? <TargetModal
          data={data}
          anchorPoint={anchorPoint}
          targetECD={targetECD}
          setTargetData={setTargetData}
          selectedJob={selectedJob}
          isOpen={showTargetModal}
          setAnchorPoint={setAnchorPoint}
          setTargetECD={setTargetECD}
          targetData={targetData}
          onSave={handleAnchorPointTargetECD}
          setParameterDirty={() => {}}
          onCloseClick={() => {
            setAnchorPoint('None');
            setTargetECD(null);
            setShowTargetModal(false);
            setTimeout(() => {
              setTargetData({});
              setSelectedRow({});
            }, 100);
          }}
          permissions={selectedRow?.isNewRow ? { permissions: HYDRAULICS_FULL_ACCESS_PERMISSIONS } : stripping}
        />
        : null
      }
    </>
  );
};
StrippingModal.propTypes = {
  selectedJob: PropTypes.object,
  rowData: PropTypes.object,
  proxyLoader: PropTypes.bool,
  setAccordionProperty: PropTypes.func,
  data: PropTypes.any,
};
StrippingModal.defaultProps = {
  selectedJob: {}
};
export default StrippingModal;
